import { all, fork, put, takeLatest, select } from '@redux-saga/core/effects';
import { createSlice, createSelector, createAction } from '@reduxjs/toolkit';
import {
  ERRORCODE_FILEUPLOAD,
  ERRORCODE_FORBIDDENACCESS,
  ERRORCODE_INVALIDMODELSTATE,
  ERRORCODE_INVALIDOPERATION,
  ERRORCODE_NOTFOUND,
  ERRORCODE_UNAUTHORIZEDACCESS,
  ERRORCODE_UNKNOWN,
  ERRORCODE_VALIDATION,
  ERRORCODE_KLEMAPPING,
  ERRORCODE_EMPTYCONCLUSION,
  ERRORCODE_SBSIPAUTHORIZATION,
  ERRORCODE_INVALIDCSVCONTENT,
} from '../constants/api';
import { getRequestDataErrors } from './helpers/selectorHelpers';
import {
  isAuthorizedSelector,
  loginFlowFinalized,
} from './authenticationSlice';

// -------- Overview --------
// This slice is responsible for global messages
const globalMessageModes = {
  loginExpired: 'LOGIN_EXPIRED',
  generalError: 'GENERAL_ERROR',
  unknownError: 'UNKNOWN_ERROR',
  notFoundError: 'NOT_FOUND_ERROR',
  forbiddenAccessError: 'FORBIDDEN_ACCESS_ERROR',
  invalidOperationError: 'INVALID_OPERATION_ERROR',
  unauthorizedAccessError: 'UNAUTHORIZED_ACCESS_ERROR',
  validationError: 'VALIDATION_ERROR',
  invalidModelStateError: 'INVALID_MODEL_STATE_ERROR',
  fileUploadError: 'FILEUPLOAD_ERROR',
  changeActiveHearingText: 'CHANGE_ACTIVE_HEARING_TEXT',
  hearingPublished: 'HEARING_PUBLISHED',
  hearingSaved: 'HEARING_SAVED',
  commentReceived: 'COMMENT_RECEIVED',
  kleValidationError: 'KLEMAPPING_VALIDATION_ERROR',
  emptyConclusionError: 'EMPTY_CONCLUSION_ERROR',
  sbsipAuthorizationError: 'SBSIP_AUTHORIZATION_ERROR',
  invalidCsvContentError: 'INVALID_CSV_CONTENT_ERROR',
  conclusionSaved: 'CONCLUSION_SAVED',
  conclusionFinalized: 'CONCLUSION_FINALIZED',
  cancelEditNotification: 'EDIT_NOTIFICATION_CANCELLED',
  downloadNotificationHasChanges: 'DOWNLOAD_NOTIFICATION_HAS_CHANGES',
  sendNotificationHasChanges: 'SEND_NOTIFICATION_HAS_CHANGES',
  sendNotificationSuccessful: 'SEND_NOTIFICATION_SUCCESSFUL',
  sendNotificationFailed: 'SEND_NOTIFICATION_FAILED',
};

const initialState = {
  mode: null,
  fileErrors: [],
  invalidKleNumbers: [],
  currentUser: null,
  fieldValidationErrors: [],
};

// --------- Actions --------
export const parseApiError = createAction('app/globalMessage/parseApiError');

// -------- Slice --------
const globalMessageSlice = createSlice({
  name: 'message',
  initialState: initialState,
  reducers: {
    setGlobalMessage(state, action) {
      state.mode = action.payload.mode || globalMessageModes.generalError;
    },
    dismissGlobalMessage() {
      return initialState;
    },
    setFileErrors(state, action) {
      state.fileErrors = action.payload;
    },
    setInvalidKleNumbers(state, action) {
      state.invalidKleNumbers = action.payload;
    },
    setFieldValidationErrors(state, action) {
      state.fieldValidationErrors = action.payload || [];
    },
  },
});

// -------- Selectors --------
const selectglobalMessageState = (state) => state.app.globalMessage;

const globalMessageModeSelector = createSelector(
  selectglobalMessageState,
  (state) => state.mode,
);

const fileErrorsSelector = createSelector(
  selectglobalMessageState,
  (state) => state.fileErrors,
);

const invalidKleNumberSelector = createSelector(
  selectglobalMessageState,
  (state) => state.invalidKleNumbers,
);

const fieldValidationErrorsSelector = createSelector(
  selectglobalMessageState,
  (state) => state.fieldValidationErrors,
);

// --------- Sagas ----------
export function* globalMessageSaga() {
  yield all([fork(parseApiErrorSaga), fork(dismissOnSuccessRelogin)]);
}

function* dismissOnSuccessRelogin() {
  yield takeLatest(loginFlowFinalized.type, function* (_) {
    const isAuthorized = yield select(isAuthorizedSelector);
    const currentMessageMode = yield select(globalMessageModeSelector);
    if (
      currentMessageMode === globalMessageModes.loginExpired &&
      isAuthorized
    ) {
      yield put(globalMessageSlice.actions.dismissGlobalMessage());
    }
  });
}

function* parseApiErrorSaga() {
  yield takeLatest(parseApiError.type, function* (action) {
    const errors = getRequestDataErrors(action);
    const [firstError] = errors || [];
    const errorCode = firstError && firstError.code;

    switch (errorCode) {
      case ERRORCODE_FILEUPLOAD:
        const fileKeys = Object.keys(firstError.meta);
        const fileNames = fileKeys.map((key) => firstError.meta[key]);
        yield put(setFileErrors(fileNames));
        yield put(
          setGlobalMessage({
            mode: globalMessageModes.fileUploadError,
          }),
        );
        break;
      case ERRORCODE_EMPTYCONCLUSION:
        yield put(
          setGlobalMessage({
            mode: globalMessageModes.emptyConclusionError,
          }),
        );
        break;
      case ERRORCODE_SBSIPAUTHORIZATION:
        yield put(
          setGlobalMessage({
            mode: globalMessageModes.sbsipAuthorizationError,
          }),
        );
        break;
      case ERRORCODE_KLEMAPPING:
        const kleNumberKeys = Object.keys(firstError.meta);
        const kleNumbers = kleNumberKeys.map((key) => firstError.meta[key]);
        yield put(setInvalidKleNumbers(kleNumbers));
        yield put(
          setGlobalMessage({
            mode: globalMessageModes.kleValidationError,
          }),
        );
        break;
      case ERRORCODE_UNKNOWN:
        yield setErrorMessage(globalMessageModes.unknownError);
        break;
      case ERRORCODE_NOTFOUND:
        yield setErrorMessage(globalMessageModes.notFoundError);
        break;
      case ERRORCODE_FORBIDDENACCESS:
        yield setErrorMessage(globalMessageModes.forbiddenAccessError);
        break;
      case ERRORCODE_INVALIDOPERATION:
        yield setErrorMessage(globalMessageModes.invalidOperationError);
        break;
      case ERRORCODE_UNAUTHORIZEDACCESS:
        yield setErrorMessage(globalMessageModes.unauthorizedAccessError);
        break;
      case ERRORCODE_VALIDATION:
        const msgKeys = Object.keys(firstError.meta);
        const validationErrors = msgKeys.map((key) => firstError.meta[key]);
        yield put(setFieldValidationErrors(validationErrors));
        yield setErrorMessage(globalMessageModes.validationError);
        break;
      case ERRORCODE_INVALIDMODELSTATE:
        yield setErrorMessage(globalMessageModes.invalidModelStateError);
        break;
      case ERRORCODE_INVALIDCSVCONTENT:
        yield setErrorMessage(globalMessageModes.invalidCsvContentError);
        break;
      default:
        yield setErrorMessage(globalMessageModes.generalError);
        break;
    }
  });
}

function setErrorMessage(
  errorMessageModeType = globalMessageModes.generalError,
) {
  return put(
    setGlobalMessage({
      mode: errorMessageModeType,
    }),
  );
}

// -------- Exporting --------
export const {
  setGlobalMessage,
  dismissGlobalMessage,
  setFileErrors,
  setInvalidKleNumbers,
  setFieldValidationErrors,
} = globalMessageSlice.actions;
export {
  globalMessageModeSelector,
  globalMessageModes,
  fileErrorsSelector,
  invalidKleNumberSelector,
  fieldValidationErrorsSelector,
};
export default globalMessageSlice.reducer;
